import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MDEditor from '@uiw/react-md-editor';
import Select from 'react-select';
import { toast } from 'react-toastify';
import StateCard from '../../components/StateCard';
import { groupCarriersByName } from '../../utils/carrierHelpers';
import { isFluxDomain } from '../../utils/fluxTheme';

function AgentRequestChange({ token }) {
  const selectedCampaign = localStorage.getItem('selectedCampaign') || 'medicare';
  const isAcaCampaign = selectedCampaign.toLowerCase().includes('aca') || selectedCampaign.toLowerCase().includes('obamacare') || selectedCampaign.toLowerCase().includes('marketplace');
  const isMedicareCampaign = !isAcaCampaign;

  const [profile, setProfile] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [formData, setFormData] = useState(null);
  const [states, setStates] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingState, setEditingState] = useState(null);
  const [showAddState, setShowAddState] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [newStateCarriers, setNewStateCarriers] = useState([]);
  const [carrierSearchTerm, setCarrierSearchTerm] = useState('');
  const [requests, setRequests] = useState([]);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [pendingRequestEmail, setPendingRequestEmail] = useState(null);

  useEffect(() => {
    fetchProfile();
    fetchStates();
    fetchCarriers();
    fetchRequests();
  }, []);

  // Check for pending requests whenever selectedMember or requests change
  useEffect(() => {
    if (requests.length > 0) {
      checkForPendingRequest();
    }
  }, [requests, selectedMember, profile]);

  const checkForPendingRequest = () => {
    const isGroup = profile?.agentType === 'group' && profile?.groupMembers && profile.groupMembers.length > 0;
    
    if (isGroup) {
      // ⭐ For groups: Check if ANY member has a pending request
      // If ANY member has pending, then ALL members are blocked from submitting
      const allMemberEmails = profile.groupMembers.map(m => m.email);
      const pendingRequestForAnyMember = requests.find(
        req => req.status === 'pending' && allMemberEmails.includes(req.agentEmail)
      );

      if (pendingRequestForAnyMember) {
        setHasPendingRequest(true);
        setPendingRequestEmail(pendingRequestForAnyMember.agentEmail);
      } else {
        setHasPendingRequest(false);
        setPendingRequestEmail(null);
      }
    } else {
      // For standalone agents: Check only their own pending requests
      const emailToCheck = profile?.email || null;
      
      if (!emailToCheck) {
        setHasPendingRequest(false);
        setPendingRequestEmail(null);
        return;
      }

      const pendingForThisEmail = requests.find(
        req => req.status === 'pending' && req.agentEmail === emailToCheck
      );

      if (pendingForThisEmail) {
        setHasPendingRequest(true);
        setPendingRequestEmail(emailToCheck);
      } else {
        setHasPendingRequest(false);
        setPendingRequestEmail(null);
      }
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get('/api/agent/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      
      // Check if this is a group
      const isGroup = response.data.agentType === 'group' && response.data.groupMembers && response.data.groupMembers.length > 0;
      
      // ⭐ Check if this is a MEMBER login (not group admin login)
      const isMemberLogin = response.data._isMemberLogin === true;
      const loggedInMemberEmail = response.data._loggedInMemberEmail;

      // ⭐ Helper: filter statesWithCarriers to only active states
      // activeStates = null/undefined → all states active (backward compat)
      const filterActiveStates = (swc, activeStates) => {
        if (!swc) return {};
        if (!activeStates) return swc; // all active
        return Object.fromEntries(
          Object.entries(swc).filter(([s]) => activeStates.includes(s))
        );
      };
      
      if (isGroup) {
        // ⭐ If member login, auto-select ONLY their member data
        if (isMemberLogin && loggedInMemberEmail) {
          const memberData = response.data.groupMembers.find(m => m.email === loggedInMemberEmail);
          if (memberData) {
            setSelectedMember(memberData);
            setFormData({
              statesWithCarriers: filterActiveStates(memberData.statesWithCarriers, memberData.activeStates),
              consent: memberData.consent || '',
              nonFfmConsent: memberData.nonFfmConsent || '',
              soaConsent: memberData.soaConsent || "",
              renewableConsent: memberData.renewableConsent || "",
              healthSherpaMarketingLink: memberData.healthSherpaMarketingLink || '',
              dentalInsuranceLink: memberData.dentalInsuranceLink || '',
              lifeInsuranceLink: memberData.lifeInsuranceLink || '',
              autoInsuranceLink: memberData.autoInsuranceLink || '',
              agentEmail: memberData.email
            });
          }
        } else {
          // Group admin login - select first member by default
          const firstMember = response.data.groupMembers[0];
          setSelectedMember(firstMember);
          setFormData({
            statesWithCarriers: filterActiveStates(firstMember.statesWithCarriers, firstMember.activeStates),
            consent: firstMember.consent || '',
            nonFfmConsent: firstMember.nonFfmConsent || '',
            soaConsent: firstMember.soaConsent || "",
            renewableConsent: firstMember.renewableConsent || "",
            healthSherpaMarketingLink: firstMember.healthSherpaMarketingLink || '',
            dentalInsuranceLink: firstMember.dentalInsuranceLink || '',
            lifeInsuranceLink: firstMember.lifeInsuranceLink || '',
            autoInsuranceLink: firstMember.autoInsuranceLink || '',
            agentEmail: firstMember.email
          });
        }
      } else {
        // For regular agents, use their own data — only active states
        setFormData({
          statesWithCarriers: filterActiveStates(response.data.statesWithCarriers, response.data.activeStates),
          consent: response.data.consent || '',
          nonFfmConsent: response.data.nonFfmConsent || '',
          soaConsent: response.data.soaConsent || "",
          renewableConsent: response.data.renewableConsent || "",
          healthSherpaMarketingLink: response.data.healthSherpaMarketingLink || '',
          dentalInsuranceLink: response.data.dentalInsuranceLink || '',
          lifeInsuranceLink: response.data.lifeInsuranceLink || '',
          autoInsuranceLink: response.data.autoInsuranceLink || ''
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await axios.get('/api/agent/states', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStates(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchCarriers = async () => {
    try {
      const response = await axios.get('/api/agent/carriers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarriers(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get('/api/agent/change-requests', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleMemberChange = (selectedOption) => {
    const member = profile.groupMembers.find(m => m.email === selectedOption.value);
    if (member) {
      setSelectedMember(member);
      // ⭐ Only show active states when member switches
      const allSwc = member.statesWithCarriers || {};
      const activeSwc = member.activeStates
        ? Object.fromEntries(Object.entries(allSwc).filter(([s]) => member.activeStates.includes(s)))
        : allSwc;
      setFormData({
        statesWithCarriers: activeSwc,
        consent: member.consent || '',
        nonFfmConsent: member.nonFfmConsent || '',
        soaConsent: member.soaConsent || "",
        renewableConsent: member.renewableConsent || "",
        healthSherpaMarketingLink: member.healthSherpaMarketingLink || '',
        dentalInsuranceLink: member.dentalInsuranceLink || '',
        lifeInsuranceLink: member.lifeInsuranceLink || '',
        autoInsuranceLink: member.autoInsuranceLink || '',
        agentEmail: member.email
      });
    }
  };

  // Helper to check if states/carriers have actually changed
  const hasChanges = () => {
    const currentData = selectedMember || profile;
    const currentStates = currentData.statesWithCarriers || {};
    const currentConsent = currentData.consent || '';
    const currentNonFfmConsent = currentData.nonFfmConsent || '';
    const currentSoaConsent = currentData.soaConsent || '';
    const currentHealthSherpaLink = currentData.healthSherpaMarketingLink || '';
    
    // Check for state/carrier changes
    const currentStateKeys = Object.keys(currentStates).sort();
    const newStateKeys = Object.keys(formData.statesWithCarriers).sort();
    
    // Different number of states = change
    if (currentStateKeys.length !== newStateKeys.length) return true;
    
    // Different state codes = change
    if (currentStateKeys.join(',') !== newStateKeys.join(',')) return true;
    
    // Check each state's carriers
    for (const state of currentStateKeys) {
      const currentCarriers = currentStates[state] || [];
      const newCarriers = formData.statesWithCarriers[state] || [];
      
      const currentIds = currentCarriers.map(c => c.CarrierId).sort().join(',');
      const newIds = newCarriers.map(c => c.CarrierId).sort().join(',');
      
      if (currentIds !== newIds) return true;
    }
    
    // Check consent changes
    if (currentConsent !== formData.consent) return true;
    if (currentNonFfmConsent !== (formData.nonFfmConsent || '')) return true;
    if (currentSoaConsent !== (formData.soaConsent || '')) return true;
    if ((currentData.renewableConsent || '') !== (formData.renewableConsent || '')) return true;
    
    // Check HealthSherpa link changes
    if (currentHealthSherpaLink !== formData.healthSherpaMarketingLink) return true;

    // Check optional insurance link changes
    if ((currentData.dentalInsuranceLink || '') !== (formData.dentalInsuranceLink || '')) return true;
    if ((currentData.lifeInsuranceLink || '') !== (formData.lifeInsuranceLink || '')) return true;
    if ((currentData.autoInsuranceLink || '') !== (formData.autoInsuranceLink || '')) return true;
    
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ⚠️ CRITICAL: Prevent submission if there's already a pending request
    if (hasPendingRequest) {
      toast.error('Cannot submit! You already have a pending change request. Please wait for admin to review it.');
      return;
    }
    
    // Validate that there are actual changes
    if (!hasChanges()) {
      toast.warning('No changes detected! Please modify states/carriers or consent before submitting.');
      return;
    }
    
    setSubmitting(true);

    try {
      // ⭐ CRITICAL FIX: Only send CHANGED states/carriers, not ALL states
      const currentData = selectedMember || profile;
      const currentStates = currentData.statesWithCarriers || {};
      const currentConsent = currentData.consent || '';
      const currentNonFfmConsent = currentData.nonFfmConsent || '';
      const currentSoaConsent = currentData.soaConsent || '';
      
      const changedStates = {};
      
      // Only include states that were actually modified or added
      for (const [state, newCarriers] of Object.entries(formData.statesWithCarriers)) {
        const currentCarriers = currentStates[state] || [];
        
        const currentIds = currentCarriers.map(c => c.CarrierId).sort().join(',');
        const newIds = newCarriers.map(c => c.CarrierId).sort().join(',');
        
        // If carriers different OR it's a new state, include it
        if (currentIds !== newIds) {
          changedStates[state] = newCarriers;
        }
      }
      
      // Also check for deleted states (states that were in current but not in formData)
      for (const state of Object.keys(currentStates)) {
        if (!formData.statesWithCarriers[state]) {
          // State was deleted - include empty array to signal deletion
          changedStates[state] = [];
        }
      }
      
      const currentHealthSherpaLink = currentData.healthSherpaMarketingLink || '';
      
      const changesToSubmit = {
        statesWithCarriers: changedStates, // Only changed states
        consent: currentConsent !== formData.consent ? formData.consent : undefined, // Only if changed
        nonFfmConsent: currentNonFfmConsent !== (formData.nonFfmConsent || '') ? formData.nonFfmConsent : undefined,
        soaConsent: currentSoaConsent !== (formData.soaConsent || '') ? formData.soaConsent : undefined,
        renewableConsent: (currentData.renewableConsent || '') !== (formData.renewableConsent || '') ? formData.renewableConsent : undefined,
        healthSherpaMarketingLink: currentHealthSherpaLink !== formData.healthSherpaMarketingLink ? formData.healthSherpaMarketingLink : undefined, // Only if changed
        dentalInsuranceLink: (currentData.dentalInsuranceLink || '') !== (formData.dentalInsuranceLink || '') ? formData.dentalInsuranceLink : undefined,
        lifeInsuranceLink: (currentData.lifeInsuranceLink || '') !== (formData.lifeInsuranceLink || '') ? formData.lifeInsuranceLink : undefined,
        autoInsuranceLink: (currentData.autoInsuranceLink || '') !== (formData.autoInsuranceLink || '') ? formData.autoInsuranceLink : undefined,
        agentEmail: formData.agentEmail
      };
      
      // Remove undefined values
      if (changesToSubmit.consent === undefined) {
        delete changesToSubmit.consent;
      }
      if (changesToSubmit.nonFfmConsent === undefined) {
        delete changesToSubmit.nonFfmConsent;
      }
      if (changesToSubmit.soaConsent === undefined) {
        delete changesToSubmit.soaConsent;
      }
      if (changesToSubmit.renewableConsent === undefined) {
        delete changesToSubmit.renewableConsent;
      }
      if (changesToSubmit.healthSherpaMarketingLink === undefined) {
        delete changesToSubmit.healthSherpaMarketingLink;
      }
      if (changesToSubmit.dentalInsuranceLink === undefined) {
        delete changesToSubmit.dentalInsuranceLink;
      }
      if (changesToSubmit.lifeInsuranceLink === undefined) {
        delete changesToSubmit.lifeInsuranceLink;
      }
      if (changesToSubmit.autoInsuranceLink === undefined) {
        delete changesToSubmit.autoInsuranceLink;
      }
      
      const submitResponse = await axios.post('/api/agent/change-requests', changesToSubmit, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // ⭐ Show different toast based on whether the request was auto-approved or needs admin review
      if (submitResponse.data?.status === 'auto_approved') {
        toast.success('✅ Changes auto-approved and applied instantly! No state deletions detected.', { autoClose: 5000 });
        // ⭐ Re-fetch profile from server to get the updated states immediately — no page refresh needed
        await fetchProfile();
      } else {
        toast.success('Change request submitted! Your request contains a state or carrier removal — it will be reviewed by admin.', { autoClose: 5000 });
        // Reset form to current (unchanged) data for pending requests — active states only
        if (selectedMember) {
          const allSwc = selectedMember.statesWithCarriers || {};
          const activeSwc = selectedMember.activeStates
            ? Object.fromEntries(Object.entries(allSwc).filter(([s]) => selectedMember.activeStates.includes(s)))
            : allSwc;
          setFormData({
            statesWithCarriers: activeSwc,
            consent: selectedMember.consent || '',
            nonFfmConsent: selectedMember.nonFfmConsent || '',
            soaConsent: selectedMember.soaConsent || '',
            renewableConsent: selectedMember.renewableConsent || '',
            healthSherpaMarketingLink: selectedMember.healthSherpaMarketingLink || '',
            dentalInsuranceLink: selectedMember.dentalInsuranceLink || '',
            lifeInsuranceLink: selectedMember.lifeInsuranceLink || '',
            autoInsuranceLink: selectedMember.autoInsuranceLink || '',
            agentEmail: selectedMember.email
          });
        } else {
          const allSwc = profile.statesWithCarriers || {};
          const activeSwc = profile.activeStates
            ? Object.fromEntries(Object.entries(allSwc).filter(([s]) => profile.activeStates.includes(s)))
            : allSwc;
          setFormData({
            statesWithCarriers: activeSwc,
            consent: profile.consent || '',
            nonFfmConsent: profile.nonFfmConsent || '',
            soaConsent: profile.soaConsent || '',
            renewableConsent: profile.renewableConsent || '',
            healthSherpaMarketingLink: profile.healthSherpaMarketingLink || '',
            dentalInsuranceLink: profile.dentalInsuranceLink || '',
            lifeInsuranceLink: profile.lifeInsuranceLink || '',
            autoInsuranceLink: profile.autoInsuranceLink || ''
          });
        }
      }
      
      fetchRequests();
    } catch (error) {
      toast.error('Error submitting change request');
    } finally {
      setSubmitting(false);
    }
  };

  const updateStateCarriers = (state, carriers) => {
    setFormData(prev => ({
      ...prev,
      statesWithCarriers: {
        ...prev.statesWithCarriers,
        [state]: carriers
      }
    }));
  };

  const deleteState = (state) => {
    const newStates = { ...formData.statesWithCarriers };
    delete newStates[state];
    setFormData(prev => ({ ...prev, statesWithCarriers: newStates }));
  };

  const addNewState = () => {
    if (selectedState && newStateCarriers.length > 0) {
      updateStateCarriers(selectedState.value, newStateCarriers);
      setSelectedState(null);
      setNewStateCarriers([]);
      setShowAddState(false);
      setCarrierSearchTerm('');
    }
  };

  if (loading || !formData) return <div className="loading">Loading...</div>;

  const isGroup = profile.agentType === 'group' && profile.groupMembers && profile.groupMembers.length > 0;
  const isMemberLogin = profile._isMemberLogin === true; // Member login (not group admin)
  
  const memberOptions = isGroup ? profile.groupMembers.map(member => ({
    value: member.email,
    label: `${member.name || member.email} (${member.email})`
  })) : [];

  const stateOptions = states
    .filter(s => !formData.statesWithCarriers[s.code])
    .map(s => ({ value: s.code, label: `${s.name} (${s.code})` }));
  
  const groupedAllCarriers = groupCarriersByName(carriers);
  const _rSelCode = selectedState?.value || '';
  const _rScMap = {AL:'alabama',AK:'alaska',AZ:'arizona',AR:'arkansas',CA:'california',CO:'colorado',CT:'connecticut',DE:'delaware',FL:'florida',GA:'georgia',HI:'hawaii',ID:'idaho',IL:'illinois',IN:'indiana',IA:'iowa',KS:'kansas',KY:'kentucky',LA:'louisiana',ME:'maine',MD:'maryland',MA:'massachusetts',MI:'michigan',MN:'minnesota',MS:'mississippi',MO:'missouri',MT:'montana',NE:'nebraska',NV:'nevada',NH:'new hampshire',NJ:'new jersey',NM:'new mexico',NY:'new york',NC:'north carolina',ND:'north dakota',OH:'ohio',OK:'oklahoma',OR:'oregon',PA:'pennsylvania',RI:'rhode island',SC:'south carolina',SD:'south dakota',TN:'tennessee',TX:'texas',UT:'utah',VT:'vermont',VA:'virginia',WA:'washington',WV:'west virginia',WI:'wisconsin',WY:'wyoming'};
  const _rAllSN = Object.values(_rScMap);
  const _rCurSN = _rScMap[_rSelCode] || '';
  const carrierOptions = groupedAllCarriers
    .filter(group => {
      if (_rSelCode && group.name) {
        const n = group.name.toLowerCase();
        for (const sn of _rAllSN) { if (sn !== _rCurSN && n.includes(sn)) return false; }
      }
      const allSelected = group.carrierIds.every(carrierId =>
        newStateCarriers.some(nc => nc.CarrierId === carrierId)
      );
      return !allSelected;
    })
    .map(group => ({
      value: group,
      label: group.name
    }));

  return (
    <div className="container">
      <h2>Request Changes</h2>
      
      <div style={{
        marginTop: '24px',
        padding: '16px',
        background: 'linear-gradient(135deg, #e0f2fe 0%, #f0fdf4 100%)',
        borderRadius: '10px',
        border: '1px solid #7dd3fc',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start'
      }}>
        <span style={{ fontSize: '22px', flexShrink: 0 }}>⚡</span>
        <div>
          <div style={{ fontWeight: '700', color: '#0369a1', marginBottom: '6px', fontSize: '15px' }}>
            Smart Auto-Approval is Active
          </div>
          <ul style={{ margin: 0, paddingLeft: '18px', color: '#1e3a5f', fontSize: '13px', lineHeight: '1.7' }}>
            <li><strong>Adding states or carriers</strong> — Changes are <span style={{ color: '#059669', fontWeight: '700' }}>auto-approved instantly</span>. Your profile updates right away. No waiting!</li>
            <li><strong>Removing a state</strong> — Requires admin review. Your request is saved and admin will approve or reject it manually.</li>
            <li><strong>Removing carriers within a state</strong> — <span style={{ color: '#dc2626', fontWeight: '700' }}>Requires admin review.</span> Carrier removals are reviewed to protect lead distribution integrity.</li>
          </ul>
        </div>
      </div>

      {/* Member Selector for Groups - Only show for group admin login, NOT for individual member login */}
      {isGroup && !isMemberLogin && (
        <div className="form-group" style={{ marginTop: '24px', padding: '16px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd', position: 'relative', zIndex: 10 }}>
          <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
            Select Group Member to Edit
          </label>
          <Select
            options={memberOptions}
            value={memberOptions.find(opt => opt.value === selectedMember?.email)}
            onChange={handleMemberChange}
            placeholder="Select a group member..."
            styles={{
              menu: (provided) => ({
                ...provided,
                zIndex: 9999
              }),
              menuPortal: (provided) => ({
                ...provided,
                zIndex: 9999
              })
            }}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
            You can submit change requests for any member in your group
          </p>
        </div>
      )}

      {/* Privacy Notice for Member Login */}
      {isGroup && isMemberLogin && selectedMember && (
        <div className="alert" style={{ marginTop: '24px', padding: '16px', background: '#e0f2fe', borderRadius: '8px', border: '1px solid #7dd3fc' }}>
          <strong>🔒 Privacy Mode:</strong> You are editing your own details ({selectedMember.name || selectedMember.email}). You cannot view or edit other members' information.
        </div>
      )}

      {/* Pending Request Warning */}
      {hasPendingRequest && (
        <div className="alert" style={{ 
          marginTop: '24px', 
          padding: '16px', 
          background: '#fef2f2', 
          borderRadius: '8px', 
          border: '2px solid #fca5a5',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <span style={{ fontSize: '24px' }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <strong style={{ color: '#991b1b',display: 'block', marginBottom: '4px' }}>
              Pending Change Request Exists
            </strong>
            <p style={{ margin: 0, color: '#7f1d1d', fontSize: '14px' }}>
              {isGroup 
                ? (pendingRequestEmail && pendingRequestEmail !== (selectedMember?.email || profile?.email)
                    ? `Another group member (${pendingRequestEmail}) has a pending change request. No members can submit new requests until it is reviewed.`
                    : `${selectedMember?.name || selectedMember?.email || 'This member'} has a pending change request. No group members can submit new requests until it is reviewed.`
                  )
                : 'You already have a pending change request.'
              }
              {' '}Please wait for admin to approve or reject the pending request before submitting a new one.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {isGroup && selectedMember && (
          <div style={{ marginTop: '16px', padding: '12px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #fde68a' }}>
            <strong>Editing:</strong> {selectedMember.name || selectedMember.email}
          </div>
        )}

        <div className="form-group" style={{ marginTop: '24px' }}>
          <div className="states-header">
            <label>States & Carriers</label>
            <button 
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => setShowAddState(!showAddState)}
            >
              {showAddState ? '✕ Cancel' : '+ Add State'}
            </button>
          </div>

          {showAddState && (
            <div className="add-state-form">
              <Select
                options={stateOptions}
                value={selectedState}
                onChange={setSelectedState}
                placeholder="Select a state..."
                className="add-state-select"
              />
              
              {selectedState && (
                <div style={{ marginTop: '12px' }}>
                  <Select
                    isMulti
                    options={carrierOptions}
                    value={groupCarriersByName(newStateCarriers).map(group => ({
                      value: group,
                      label: group.name
                    }))}
                    onChange={(selected) => {
                      if (!selected || selected.length === 0) {
                        setNewStateCarriers([]);
                      } else {
                        const newCarriers = [];
                        selected.forEach(item => {
                          item.value.carrierIds.forEach(carrierId => {
                            const carrier = carriers.find(c => c.CarrierId === carrierId);
                            if (carrier) {
                              newCarriers.push(carrier);
                            }
                          });
                        });
                        setNewStateCarriers(newCarriers);
                      }
                    }}
                    placeholder="Search and select carriers..."
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={addNewState}
                    disabled={!selectedState || newStateCarriers.length === 0}
                    style={{ marginTop: '8px' }}
                  >
                    ✓ Add {selectedState.value} with {newStateCarriers.length} carrier{newStateCarriers.length !== 1 ? 's' : ''}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="states-list" style={{marginTop: '10px'}}>
            {Object.keys(formData.statesWithCarriers).length === 0 ? (
              <div className="empty-state-message">
                No states added yet. Click "+ Add State" to begin.
              </div>
            ) : (
              Object.entries(formData.statesWithCarriers).map(([state, stateCarriers]) => (
                <StateCard
                  key={state}
                  stateCode={state}
                  carriers={stateCarriers}
                  allCarriers={carriers}
                  onUpdate={updateStateCarriers}
                  onDelete={deleteState}
                  isEditing={editingState === state}
                  setIsEditing={(editing) => setEditingState(editing ? state : null)}
                  isAdminView={false}
                />
              ))
            )}
          </div>
        </div>

       {/* {!isFluxDomain() ? */}
        <div className="form-group" style={{marginTop: '24px'}}>
          <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
            🔗 HealthSherpa Marketing Link
          </label>
          <input
            type="url"
            value={formData.healthSherpaMarketingLink}
            onChange={(e) => setFormData({ ...formData, healthSherpaMarketingLink: e.target.value })}
            placeholder="https://example.healthsherpa.com/agent123"
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              fontSize: '14px'
            }}
          />
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', marginBottom: 0 }}>
            Enter your HealthSherpa marketing link. This will be used for opportunity referrals.
          </p>
        </div>

        <div style={{ marginTop: 20, padding: '14px 16px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0369a1', marginBottom: 12 }}>📋 Optional Insurance Links (shown to your clients)</div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>🦷 Dental Insurance Link (Optional)</label>
            <input
              type="url"
              value={formData.dentalInsuranceLink || ''}
              onChange={(e) => setFormData({ ...formData, dentalInsuranceLink: e.target.value })}
              placeholder="https://dental-signup-link.com"
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>❤️ Life Insurance Link (Optional)</label>
            <input
              type="url"
              value={formData.lifeInsuranceLink || ''}
              onChange={(e) => setFormData({ ...formData, lifeInsuranceLink: e.target.value })}
              placeholder="https://life-insurance-link.com"
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>🚗 Auto Insurance Link (Optional)</label>
            <input
              type="url"
              value={formData.autoInsuranceLink || ''}
              onChange={(e) => setFormData({ ...formData, autoInsuranceLink: e.target.value })}
              placeholder="https://auto-insurance-link.com"
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
            />
          </div>
        </div>
         {/* : <></>} */}

        <div className="form-group" style={{marginTop: '24px'}}>
          <label>Consent (Markdown)</label>
          <MDEditor
            value={formData.consent}
            onChange={(value) => setFormData({ ...formData, consent: value || '' })}
            height={300}
          />
        </div>

        {isAcaCampaign && (
          <div className="form-group" style={{marginTop: '24px'}}>
            <label>Non-FFM Consent (Markdown) <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>— ACA Agents Only</span></label>
            <MDEditor
              value={formData.nonFfmConsent || ''}
              onChange={(value) => setFormData({ ...formData, nonFfmConsent: value || '' })}
              height={300}
            />
          </div>
        )}

        {isMedicareCampaign && (
          <div className="form-group" style={{marginTop: '24px'}}>
            <label>SOA Consent (Markdown) <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>— Medicare Agents Only</span></label>
            <MDEditor
              value={formData.soaConsent || ''}
              onChange={(value) => setFormData({ ...formData, soaConsent: value || '' })}
              height={300}
            />
          </div>
        )}

        <div className="form-group" style={{marginTop: '24px'}}>
          <label>Renewable Consent (Markdown) <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>— Shown to returning leads eligible for re-enrollment</span></label>
          <MDEditor
            value={formData.renewableConsent || ''}
            onChange={(value) => setFormData({ ...formData, renewableConsent: value || '' })}
            height={200}
          />
        </div>

        <button 
          type="submit" 
          className="btn btn-primary btn-block" 
          disabled={submitting || hasPendingRequest} 
          style={{marginTop: '24px'}}
          title={hasPendingRequest ? 'Cannot submit - pending request exists' : ''}
        >
          {submitting ? 'Submitting...' : 
           hasPendingRequest ? '⚠️ Pending Request Exists - Cannot Submit' :
           `Submit Change Request${isGroup ? ` for ${selectedMember?.name || selectedMember?.email}` : ''}`}
        </button>
      </form>

      <div style={{marginTop: '48px'}}>
        <h3>My Change Requests</h3>
        <div className="request-list">
          {requests.length === 0 ? (
            <div className="empty-state">No change requests yet</div>
          ) : (
            requests.map(request => (
              <div key={request.id} className="request-card" style={{
                borderLeft: request.status === 'auto_approved'
                  ? '4px solid #0ea5e9'
                  : request.status === 'approved'
                  ? '4px solid #10b981'
                  : request.status === 'pending'
                  ? '4px solid #f59e0b'
                  : request.status === 'rejected'
                  ? '4px solid #ef4444'
                  : undefined
              }}>
                <div className="request-header">
                  <div>
                    {request.agentEmail && (
                      <p style={{ fontSize: '14px', color: '#4f46e5', margin: 0 }}>
                        Agent: {request.agentEmail}
                      </p>
                    )}
                    {request.status === 'auto_approved' ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '3px 10px', background: '#e0f2fe', color: '#0369a1',
                        borderRadius: '20px', fontSize: '12px', fontWeight: '600',
                        border: '1px solid #7dd3fc'
                      }}>
                        ⚡ Approved
                      </span>
                    ) : (
                      <span className={`status-badge status-${request.status}`}>
                        {request.status}
                      </span>
                    )}
                  </div>
                  <span className="request-date">
                    {new Date(request.createdAt).toLocaleString()}
                  </span>
                </div>
                {/* {request.status === 'auto_approved' && (
                  <p style={{ fontSize: '13px', color: '#0369a1', margin: '8px 0 0' }}>
                    ⚡ Your changes were automatically applied — no state deletions detected.
                  </p>
                )} */}
                {request.status === 'rejected' && request.rejectionReason && (
                  <div className="rejection-reason">
                    <strong>Rejection Reason:</strong> {request.rejectionReason}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default AgentRequestChange;
